import {
  createProductFn,
  deleteProductFn,
  listAllProductsOptions,
  listPublicProductsOptions,
  toggleActiveFn,
  toggleFeaturedFn,
  updateProductFn,
} from "@/actions/product/handler";
import {
  ProductType,
  type SelectProduct,
  getProductTypeName,
} from "@/api/db/schema/product";
import { ProductForm } from "@/components/admin/ProductForm";
import { ProductList } from "@/components/admin/ProductList";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  redirect,
  useRouteContext,
} from "@tanstack/react-router";
import { ArrowLeft, Dumbbell, Package, Plus, Shirt, Watch } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/store")({
  beforeLoad: async ({ context }) => {
    if (!context.user || context.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context }) =>
    await context.queryClient.ensureQueryData(listAllProductsOptions()),
  component: RouteComponent,
});

function RouteComponent() {
  const { data } = useSuspenseQuery(listAllProductsOptions());
  const { queryClient } = useRouteContext({ from: "/_app/admin/store" });

  const [activeTab, setActiveTab] = useState<
    "tshirts" | "weights" | "equipment" | "accessories" | "other"
  >("tshirts");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<SelectProduct | null>(
    null,
  );
  const [deleteProduct, setDeleteProduct] = useState<SelectProduct | null>(
    null,
  );

  // Filter products by type
  const tshirts =
    data.body?.products?.filter((p) => p.productType === ProductType.TSHIRT) ||
    [];
  const weights =
    data.body?.products?.filter((p) => p.productType === ProductType.WEIGHTS) ||
    [];
  const equipment =
    data.body?.products?.filter(
      (p) => p.productType === ProductType.EQUIPMENT,
    ) || [];
  const accessories =
    data.body?.products?.filter(
      (p) => p.productType === ProductType.ACCESSORIES,
    ) || [];
  const other =
    data.body?.products?.filter((p) => p.productType === ProductType.OTHER) ||
    [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: createProductFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllProductsOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: listPublicProductsOptions().queryKey,
      });
      toast.success("Ürün başarıyla oluşturuldu");
      setIsFormOpen(false);
      setEditingProduct(null);
    },
    onError: (error) => {
      toast.error("Ürün oluşturulamadı", {
        description: error.message,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateProductFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllProductsOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: listPublicProductsOptions().queryKey,
      });
      toast.success("Ürün başarıyla güncellendi");
      setIsFormOpen(false);
      setEditingProduct(null);
    },
    onError: (error) => {
      toast.error("Ürün güncellenemedi", {
        description: error.message,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteProductFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllProductsOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: listPublicProductsOptions().queryKey,
      });
      toast.success("Ürün başarıyla silindi");
      setDeleteProduct(null);
    },
    onError: (error) => {
      toast.error("Ürün silinemedi", {
        description: error.message,
      });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: toggleActiveFn,
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: listAllProductsOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: listPublicProductsOptions().queryKey,
      });
      toast.success(response.body?.message || "Durum değiştirildi");
    },
    onError: (error) => {
      toast.error("Durum değiştirilemedi", {
        description: error.message,
      });
    },
  });

  const toggleFeaturedMutation = useMutation({
    mutationFn: toggleFeaturedFn,
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: listAllProductsOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: listPublicProductsOptions().queryKey,
      });
      toast.success(
        response.body?.message || "Öne çıkarma durumu değiştirildi",
      );
    },
    onError: (error) => {
      toast.error("Öne çıkarma durumu değiştirilemedi", {
        description: error.message,
      });
    },
  });

  // Handlers
  const getProductTypeFromTab = () => {
    switch (activeTab) {
      case "tshirts":
        return ProductType.TSHIRT;
      case "weights":
        return ProductType.WEIGHTS;
      case "equipment":
        return ProductType.EQUIPMENT;
      case "accessories":
        return ProductType.ACCESSORIES;
      default:
        return ProductType.OTHER;
    }
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setIsFormOpen(true);
  };

  const handleEdit = (product: SelectProduct) => {
    setEditingProduct(product);
    setIsFormOpen(true);
  };

  const handleDelete = (product: SelectProduct) => {
    setDeleteProduct(product);
  };

  const handleToggleActive = (product: SelectProduct) => {
    toggleActiveMutation.mutate({ data: { productId: product.productId } });
  };

  const handleToggleFeatured = (product: SelectProduct) => {
    toggleFeaturedMutation.mutate({ data: { productId: product.productId } });
  };

  const handleFormSubmit = async (formData: {
    productType: ProductType;
    data: any;
    displayOrder?: number;
  }) => {
    if (editingProduct) {
      await updateMutation.mutateAsync({
        data: {
          productId: editingProduct.productId,
          ...formData,
        },
      });
    } else {
      await createMutation.mutateAsync({ data: formData });
    }
  };

  const handleConfirmDelete = () => {
    if (deleteProduct) {
      deleteMutation.mutate({ data: { productId: deleteProduct.productId } });
    }
  };

  const currentProductType = getProductTypeFromTab();

  return (
    <div className="container mx-auto max-w-7xl space-y-8 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      {/* Breadcrumb */}
      <div className="mb-6">
        <Button asChild variant="ghost" className="mb-4">
          <Link to="/admin">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Admin Paneli
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-bold text-2xl sm:text-3xl">Ürün Yönetimi</h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Mağaza ürünlerini yönetin
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="tshirts" className="flex-1 gap-2 sm:flex-none">
              <Shirt className="h-4 w-4" />
              <span className="hidden sm:inline">
                {getProductTypeName(ProductType.TSHIRT)}
              </span>
              <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs">
                {tshirts.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="weights" className="flex-1 gap-2 sm:flex-none">
              <Dumbbell className="h-4 w-4" />
              <span className="hidden sm:inline">
                {getProductTypeName(ProductType.WEIGHTS)}
              </span>
              <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs">
                {weights.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="equipment"
              className="flex-1 gap-2 sm:flex-none"
            >
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">
                {getProductTypeName(ProductType.EQUIPMENT)}
              </span>
              <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs">
                {equipment.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="accessories"
              className="hidden flex-1 gap-2 sm:flex sm:flex-none"
            >
              <Watch className="h-4 w-4" />
              <span className="hidden sm:inline">
                {getProductTypeName(ProductType.ACCESSORIES)}
              </span>
              <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs">
                {accessories.length}
              </span>
            </TabsTrigger>
          </TabsList>

          <Button onClick={() => handleCreate()} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">
              Yeni {getProductTypeName(currentProductType)}
            </span>
            <span className="sm:hidden">Yeni Ürün</span>
          </Button>
        </div>

        <TabsContent value="tshirts" className="mt-6">
          <ProductList
            products={tshirts}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onToggleFeatured={handleToggleFeatured}
            isLoading={{
              delete: deleteMutation.isPending
                ? deleteProduct?.productId
                : undefined,
              toggleActive: toggleActiveMutation.isPending
                ? toggleActiveMutation.variables?.data.productId
                : undefined,
              toggleFeatured: toggleFeaturedMutation.isPending
                ? toggleFeaturedMutation.variables?.data.productId
                : undefined,
            }}
          />
        </TabsContent>

        <TabsContent value="weights" className="mt-6">
          <ProductList
            products={weights}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onToggleFeatured={handleToggleFeatured}
            isLoading={{
              delete: deleteMutation.isPending
                ? deleteProduct?.productId
                : undefined,
              toggleActive: toggleActiveMutation.isPending
                ? toggleActiveMutation.variables?.data.productId
                : undefined,
              toggleFeatured: toggleFeaturedMutation.isPending
                ? toggleFeaturedMutation.variables?.data.productId
                : undefined,
            }}
          />
        </TabsContent>

        <TabsContent value="equipment" className="mt-6">
          <ProductList
            products={equipment}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onToggleFeatured={handleToggleFeatured}
            isLoading={{
              delete: deleteMutation.isPending
                ? deleteProduct?.productId
                : undefined,
              toggleActive: toggleActiveMutation.isPending
                ? toggleActiveMutation.variables?.data.productId
                : undefined,
              toggleFeatured: toggleFeaturedMutation.isPending
                ? toggleFeaturedMutation.variables?.data.productId
                : undefined,
            }}
          />
        </TabsContent>

        <TabsContent value="accessories" className="mt-6">
          <ProductList
            products={accessories}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onToggleFeatured={handleToggleFeatured}
            isLoading={{
              delete: deleteMutation.isPending
                ? deleteProduct?.productId
                : undefined,
              toggleActive: toggleActiveMutation.isPending
                ? toggleActiveMutation.variables?.data.productId
                : undefined,
              toggleFeatured: toggleFeaturedMutation.isPending
                ? toggleFeaturedMutation.variables?.data.productId
                : undefined,
            }}
          />
        </TabsContent>

        <TabsContent value="other" className="mt-6">
          <ProductList
            products={other}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onToggleFeatured={handleToggleFeatured}
            isLoading={{
              delete: deleteMutation.isPending
                ? deleteProduct?.productId
                : undefined,
              toggleActive: toggleActiveMutation.isPending
                ? toggleActiveMutation.variables?.data.productId
                : undefined,
              toggleFeatured: toggleFeaturedMutation.isPending
                ? toggleFeaturedMutation.variables?.data.productId
                : undefined,
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? "Ürünü Düzenle" : "Yeni Ürün Oluştur"}
            </DialogTitle>
          </DialogHeader>
          <ProductForm
            initialData={editingProduct || undefined}
            productType={currentProductType}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setIsFormOpen(false);
              setEditingProduct(null);
            }}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteProduct}
        onOpenChange={(open) => !open && setDeleteProduct(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ürünü Sil</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteProduct?.data.name} ürününü silmek istediğinize emin
              misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
